import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ChatComposer } from "@/components/chat/thread/ChatComposer";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UnderlineTabs, type UnderlineTabOption } from "@/components/ui/underline-tabs";
import type { UserAvatarUser } from "@/components/ui/user-avatar";
import { usePermissions } from "@/hooks/usePermissions";
import { usePermissionDenied } from "@/hooks/usePermissionDenied";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { useDocumentCommentMutations } from "@/stores/documents/comments/mutations";
import {
  useDocumentThreadsQuery,
  useMentionAudienceQuery,
  useThreadScopeQuery,
} from "@/stores/documents/comments/queries";
import type {
  CommentSource,
  DocumentComment,
  DocumentThread,
  DocumentThreadLinkedIssue,
} from "@/stores/documents/comments/types";
import { useOptionalStores } from "@/stores/useStores";
import { composerLabels } from "./commentsLabels";
import { DocumentThreadCard } from "./DocumentThreadCard";
import { deriveMentionIds, mentionDisplayNames, toMentionUsers } from "./mentionAudience";

export interface DocumentCommentsThreadProps {
  documentId: number;
  buildingId: number;
  /** The page the paged renderer is showing; null / undefined when none. */
  currentPage?: number | null;
  /** True when the host can scroll to a page (pdf.js renderer succeeded) or
   *  show a sheet (the spreadsheet renderer parsed the workbook). */
  pagedRenderer: boolean;
  /** What `currentPage` counts: PDF pages, or a spreadsheet's sheets. */
  anchorKind?: "page" | "sheet";
  /** The sheet's name, for the switch's label. */
  anchorName?: string | null;
  onGoToPage?: (page: number, documentId: number) => void;
  /** The host can open ANOTHER version's document too (the list panel), not
   *  only scroll the one it is showing (the viewer rail). */
  crossVersionNavigation?: boolean;
  /** Deep link: scroll to and highlight this thread once loaded. */
  initialThreadId?: number | null;
  /** Read without writing (a document in the trash, no session). */
  readOnly?: boolean;
  className?: string;
}

type Tab = "open" | "resolved";

const HIGHLIGHT_MS = 2500;

/**
 * The comments surface shared by the list panel and the viewer rail: a
 * composer for a new thread (anchored to the current page when there is one,
 * filed as a sub-issue unless the writer unticks it), Open / Completed tabs,
 * and one `DocumentThreadCard` per thread across the document's whole
 * version lineage.
 */
export const DocumentCommentsThread = observer(function DocumentCommentsThread({
  documentId,
  buildingId,
  currentPage = null,
  pagedRenderer,
  anchorKind = "page",
  anchorName = null,
  onGoToPage,
  crossVersionNavigation = false,
  initialThreadId = null,
  readOnly = false,
  className,
}: DocumentCommentsThreadProps) {
  const { t } = useLanguage();
  const c = t.documents.comments;
  const navigate = useNavigate();
  const stores = useOptionalStores();
  const userProfile = stores?.userStore.userProfile ?? null;
  const { canModifyData, canManagePMOnProject } = usePermissions();
  const currentUserId = userProfile?.id ?? null;
  const currentUser: UserAvatarUser | null = userProfile
    ? {
        id: userProfile.id,
        first_name: userProfile.first_name ?? null,
        last_name: userProfile.last_name ?? null,
        email: userProfile.email,
        avatar_url: userProfile.avatar_url ?? null,
        color: userProfile.color ?? null,
      }
    : null;

  const scopeQuery = useThreadScopeQuery(documentId);
  const scope = scopeQuery.data ?? null;
  const threadsQuery = useDocumentThreadsQuery(scope, currentUserId);
  const mutations = useDocumentCommentMutations(scope, currentUserId);
  const audience = useMentionAudienceQuery(documentId);
  const mentionUsers = useMemo(() => toMentionUsers(audience), [audience]);
  const displayNames = useMemo(() => mentionDisplayNames(audience), [audience]);

  const effectiveReadOnly = readOnly || currentUserId == null || Boolean(scope?.isDeleted);
  // Filing an issue needs a project to file it under and MANAGE_PM there. The
  // composer's "create issue" tick is only offered with MANAGE_PM (a comment
  // without one is still a comment); a thread's "Create issue" button is
  // offered on any project and refused on the press with a message.
  const canFileIssue =
    scope?.projectId != null && !effectiveReadOnly && canManagePMOnProject(scope.projectId);
  const offerFileIssue = scope?.projectId != null && !effectiveReadOnly;
  const denyPermission = usePermissionDenied();

  // --- tabs + deep link -----------------------------------------------------
  const [tab, setTab] = useState<Tab>("open");
  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  const handledInitialRef = useRef<number | null>(null);
  useEffect(() => {
    if (initialThreadId == null || threadsQuery.isLoading) return;
    if (handledInitialRef.current === initialThreadId) return;
    const target = threadsQuery.threads.find((th) => th.id === initialThreadId);
    if (!target) return;
    handledInitialRef.current = initialThreadId;
    setTab(target.resolvedAt ? "resolved" : "open");
    setHighlightedId(target.id);
    const timer = window.setTimeout(() => {
      document
        .querySelector(`[data-thread-id="${target.id}"]`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 0);
    const clear = window.setTimeout(() => setHighlightedId(null), HIGHLIGHT_MS);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clear);
    };
  }, [initialThreadId, threadsQuery.isLoading, threadsQuery.threads]);

  // --- mark my mentions read as they render ----------------------------------
  const markedRef = useRef(new Set<number>());
  const markRead = mutations.markRead;
  useEffect(() => {
    if (currentUserId == null) return;
    const items: Array<{ commentId: number; source: CommentSource }> = [];
    for (const th of threadsQuery.threads) {
      for (const comment of [th.root, ...th.replies]) {
        if (comment.unreadForMe && !markedRef.current.has(comment.id)) {
          items.push({ commentId: comment.id, source: th.commentSource });
        }
      }
    }
    if (items.length === 0) return;
    for (const item of items) markedRef.current.add(item.commentId);
    void markRead(items)
      .then(() => {
        // Both dots: the Documents one (opted-out threads) and the PM one
        // (issue-backed threads, whose mentions are issue mentions).
        window.dispatchEvent(new Event("franklin:document-mentions-read"));
        window.dispatchEvent(new Event("franklin:pm-mentions-read"));
      })
      .catch(() => {
        // Best-effort: the dots re-read on the next realtime signal.
      });
  }, [threadsQuery.threads, currentUserId, markRead]);

  // --- new thread ---------------------------------------------------------------
  const [anchorToPage, setAnchorToPage] = useState(true);
  const pageAnchor = pagedRenderer && currentPage != null && anchorToPage ? currentPage : null;
  // Opt-OUT: a new thread is filed as a sub-issue unless the writer unticks it.
  const [createIssue, setCreateIssue] = useState(true);

  const failToast = useCallback(
    (error: unknown) => {
      toast.error(error instanceof Error && error.message ? error.message : c.writeFailed);
    },
    [c.writeFailed]
  );

  const handleNewThread = (body: string, mentionedUserIds: number[]) => {
    void mutations
      .createThread({
        pageNumber: pageAnchor,
        body,
        mentionedUserIds,
        createIssue: canFileIssue && createIssue,
      })
      .then(() => setTab("open"))
      .catch(failToast);
  };

  const openIssue = (issue: DocumentThreadLinkedIssue) => {
    // Carry the project: a cold Issues page with no scope chosen otherwise
    // lands on "Choose a project" instead of the issue.
    const project = issue.projectId != null ? `project=${issue.projectId}&` : "";
    navigate(`/buildings/${buildingId}/issues?${project}selected=${issue.id}`);
  };

  const threadTarget = (thread: DocumentThread) => ({
    id: thread.id,
    issueId: thread.linkedIssue?.id ?? null,
  });

  // The switch beside "Create issue": a PDF anchors to a page, a spreadsheet to a sheet.
  const anchorLabel =
    anchorKind === "sheet"
      ? c.sheetAnchorToggle.replace("{name}", anchorName ?? String(currentPage ?? ""))
      : c.pageAnchorToggle.replace("{page}", String(currentPage ?? ""));
  const anchorOnHint =
    anchorKind === "sheet"
      ? c.commentOnSheet.replace("{name}", anchorName ?? String(currentPage ?? ""))
      : c.commentOnPage.replace("{page}", String(currentPage ?? ""));

  // --- render ---------------------------------------------------------------------
  const goToPage = (page: number, docId: number) => onGoToPage?.(page, docId);
  const visible = tab === "open" ? threadsQuery.open : threadsQuery.resolved;
  const tabOptions: Array<UnderlineTabOption<Tab>> = [
    { value: "open", label: c.openCount.replace("{count}", String(threadsQuery.open.length)) },
    {
      value: "resolved",
      label: c.resolvedCount.replace("{count}", String(threadsQuery.resolved.length)),
    },
  ];

  if (scopeQuery.isLoading || (scope && threadsQuery.isLoading)) {
    return (
      <div className={cn("space-y-3 p-4", className)}>
        <Skeleton className="h-10 w-full rounded-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (threadsQuery.unavailable) {
    return <p className={cn("p-4 text-sm text-muted-foreground", className)}>{c.unavailable}</p>;
  }
  if (!scope || threadsQuery.isError) {
    return <p className={cn("p-4 text-sm text-destructive", className)}>{c.loadError}</p>;
  }

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      {!effectiveReadOnly && (
        <div className="shrink-0 border-b px-3 pb-3 pt-3">
          <ChatComposer
            buildingId={buildingId}
            projectId={scope.projectId ?? 0}
            currentUser={currentUser}
            mentionUsers={mentionUsers}
            attachments={false}
            onSend={handleNewThread}
            labels={composerLabels(c, c.placeholder)}
          />
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
            {scope.projectId != null && canFileIssue && (
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <label className="flex w-fit cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                      <Checkbox
                        checked={createIssue}
                        onCheckedChange={(v) => setCreateIssue(v === true)}
                        aria-label={c.createIssueOption}
                      />
                      {c.createIssueOption}
                    </label>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-xs">
                    {c.createIssueOptionHint}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* On = the comment is pinned to the page (or sheet) being read;
                off = it is about the whole document. A standard switch, red
                when on like every other highlight on these surfaces. */}
            {pagedRenderer && currentPage != null && (
              <label
                className="flex w-fit cursor-pointer items-center gap-2 text-xs text-muted-foreground"
                title={anchorToPage ? anchorOnHint : c.commentOnDocument}
              >
                <Switch
                  checked={anchorToPage}
                  onCheckedChange={setAnchorToPage}
                  aria-label={anchorLabel}
                  className="origin-left scale-75 data-[state=checked]:bg-red-600"
                />
                <span className="-ml-2">{anchorLabel}</span>
              </label>
            )}
          </div>
          {scope.projectId == null && (
            <p className="mt-2 text-[11px] text-muted-foreground">{c.noProjectForIssue}</p>
          )}
        </div>
      )}

      <div className="shrink-0 px-3 pt-1">
        <UnderlineTabs value={tab} onValueChange={(v) => setTab(v as Tab)} options={tabOptions} />
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {visible.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-muted-foreground">
            {tab === "open" ? c.emptyOpen : c.emptyResolved}
          </p>
        ) : (
          visible.map((thread) => (
            <DocumentThreadCard
              key={thread.id}
              thread={thread}
              currentUserId={currentUserId}
              currentUser={currentUser}
              canModifyData={canModifyData}
              canCreateIssue={offerFileIssue}
              viewingDocumentId={scope.id}
              // The rail can only scroll the file it is showing, and only when
              // pdf.js is driving it; the list panel opens the viewer at the page
              // for any version, so every badge there is live.
              canGoToPage={
                Boolean(onGoToPage) &&
                (crossVersionNavigation || (thread.documentId === scope.id && pagedRenderer))
              }
              buildingId={buildingId}
              audience={audience}
              mentionUsers={mentionUsers}
              mentionDisplayNames={displayNames}
              readOnly={effectiveReadOnly}
              busy={mutations.isPending}
              highlighted={highlightedId === thread.id}
              onGoToPage={goToPage}
              onReply={(th, body, ids) =>
                mutations
                  .reply({
                    target: { threadId: th.id, issueId: th.linkedIssue?.id ?? null },
                    body,
                    mentionedUserIds: ids,
                  })
                  .catch(failToast)
              }
              onEdit={(th, comment, body, ids) =>
                mutations
                  .edit({
                    commentId: comment.id,
                    source: th.commentSource,
                    body,
                    mentionedUserIds: ids,
                  })
                  .catch(failToast)
              }
              onDeleteReply={(th, comment) =>
                mutations
                  .remove({ commentId: comment.id, source: th.commentSource })
                  .catch(failToast)
              }
              onDeleteThread={(th) => mutations.removeThread(th.id).catch(failToast)}
              onResolve={(th) => mutations.resolve(threadTarget(th)).catch(failToast)}
              onReopen={(th) =>
                mutations
                  .reopen(threadTarget(th))
                  .then(() => setTab("open"))
                  .catch(failToast)
              }
              onCreateIssue={async (th) =>
                canFileIssue
                  ? mutations.promote(th.id).catch(failToast)
                  : denyPermission("manageIssues", scope.projectId ?? null)
              }
              onOpenIssue={openIssue}
            />
          ))
        )}
      </div>
    </div>
  );
});
